import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";
import { LayoutDashboard, ArrowRightLeft, CreditCard, Stethoscope, GraduationCap, Upload, Newspaper } from "lucide-react";
import Sidebar from "@/components/app/Sidebar";
import Overview from "@/components/app/Overview";
import Transactions from "@/components/app/Transactions";
import Debts from "@/components/app/Debts";
import Diagnosis from "@/components/app/Diagnosis";
import Education from "@/components/app/Education";
import ImportStatement from "@/components/app/ImportStatement";
import News from "@/components/app/News";
import { useAuth } from "@/context/AuthContext";

const TITLES = {
  overview: "Dashboard", transactions: "Transações", debts: "Dívidas", diagnosis: "Diagnóstico Financeiro",
  education: "Educação Financeira", import: "Importar Extrato", news: "Notícias Econômicas",
};
const MOBILE = [
  { id: "overview", icon: LayoutDashboard }, { id: "transactions", icon: ArrowRightLeft },
  { id: "debts", icon: CreditCard }, { id: "diagnosis", icon: Stethoscope },
  { id: "education", icon: GraduationCap }, { id: "import", icon: Upload }, { id: "news", icon: Newspaper },
];

export default function Dashboard() {
  const [active, setActive] = useState("overview");
  const [scope, setScope] = useState("personal");
  const { user } = useAuth();

  const sections = {
    overview: <Overview scope={scope} setScope={setScope} />,
    transactions: <Transactions scope={scope} setScope={setScope} />,
    debts: <Debts scope={scope} setScope={setScope} />,
    diagnosis: <Diagnosis scope={scope} setScope={setScope} />,
    education: <Education scope={scope} setScope={setScope} />,
    import: <ImportStatement scope={scope} setScope={setScope} />,
    news: <News />,
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar active={active} setActive={setActive} />
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="flex items-center gap-4 px-5 lg:px-8 pt-6 pb-2">
          <div className="md:hidden h-10 w-10 rounded-2xl bg-surface border border-border flex items-center justify-center overflow-hidden">
            <img src="/alligare-logo.png" alt="Alligare" data-testid="brand-icon-mobile" className="h-7 w-7 object-contain" />
          </div>
          <div className="flex-1">
            <p className="uppercase text-[11px] tracking-[0.2em] text-text-muted font-body">Alligare · Finanças</p>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-text-primary">{TITLES[active]}</h1>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-surface border border-border rounded-full px-4 py-2.5 w-64">
            <Search className="h-4 w-4 text-text-muted" />
            <input data-testid="topbar-search" placeholder="Buscar..." className="bg-transparent outline-none text-sm font-body w-full text-text-primary placeholder:text-text-muted" />
          </div>
          <div className="h-11 w-11 rounded-full bg-primary text-white flex items-center justify-center font-heading font-bold shrink-0">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </div>
        </header>

        {/* Mobile nav */}
        <div className="md:hidden flex gap-2 px-5 py-3 overflow-x-auto">
          {MOBILE.map((m) => {
            const Icon = m.icon; const on = active === m.id;
            return (
              <button key={m.id} data-testid={`mnav-${m.id}`} onClick={() => setActive(m.id)}
                className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${on ? "bg-primary text-white" : "bg-surface border border-border text-text-secondary"}`}>
                <Icon className="h-5 w-5" />
              </button>
            );
          })}
        </div>

        <div className="flex-1 px-5 lg:px-8 py-4 pb-10">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
              {sections[active]}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
